import { ZObjectItem } from "src/types/ZObjectItem";
import { ZUid } from "src/types/zUid";
import { loadObject } from "src/common/api/apiObject";
import { CacheGuard } from "./cacheGuard";
import { Resource } from "./resource";
import { ResourceState } from "./resourceState";

/**
 * Полные данные объектов.
 *
 * Ключ кэша (версия модели, пользователь, язык) не входит в ключ ресурса:
 * иначе записи под старыми ключами копились бы в памяти. Вместо этого ключ
 * проверяется в момент обращения - то есть по действию пользователя, как и
 * было решено: фоновых перезагрузок нет.
 */
export class ObjectDetailsStore {
  private readonly resource = new Resource<ZUid, ZObjectItem>(
    // Без перевода: имя объекта - ключ переводов, и форма правит именно его
    (objectId) => loadObject(objectId),
    String,
  );

  private readonly guard: CacheGuard;

  constructor(cacheKey: () => string) {
    this.guard = new CacheGuard(cacheKey);
  }

  state(objectId: ZUid): ResourceState<ZObjectItem> {
    return this.resource.state(objectId);
  }

  /** Готовые данные, если они есть. Загрузку не запускает. */
  peek(objectId: ZUid): ZObjectItem | undefined {
    const state = this.resource.state(objectId);
    return state.status === "ready" ? state.value : undefined;
  }

  /**
   * Данные объекта: из памяти либо из сети.
   * Если с момента загрузки сменились версия модели, пользователь или язык,
   * данные считаются устаревшими и перечитываются.
   */
  async ensure(objectId: ZUid): Promise<ZObjectItem> {
    if (this.guard.isStale(objectId)) this.resource.invalidate(objectId);
    return this.resource.get(objectId);
  }

  /** Данные пришли иным путём: создание, копирование, импорт, сохранение. */
  set(objectId: ZUid, details: ZObjectItem) {
    this.resource.set(objectId, details);
    this.guard.remember(objectId);
  }

  clear() {
    this.resource.clear();
    this.guard.clear();
  }

  forget(objectId: ZUid) {
    this.resource.invalidate(objectId);
    this.guard.forget(objectId);
  }
}
